'use client'

import * as React from "react"
import { motion } from 'framer-motion'
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { prefersReducedMotion } from "@/lib/animations"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-sans font-semibold text-button transition-all duration-base disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "bg-off-black text-off-white hover:bg-off-black/90 focus:ring-2 focus:ring-teal/50 focus:outline-none rounded-md shadow-sm hover:shadow-md",
        cta:
          "bg-orange-cta text-white hover:bg-orange-hover active:bg-orange-active focus:ring-2 focus:ring-orange-cta/30 focus:outline-none rounded-md shadow-sm hover:shadow-md",
        primary:
          "bg-teal text-off-black hover:bg-teal-dark rounded-md shadow-sm hover:shadow-md",
        secondary:
          "bg-light-purple text-off-black hover:bg-light-purple-dark focus:ring-2 focus:ring-light-purple/50 focus:outline-none rounded-md shadow-sm hover:shadow-md",
        teal:
          "bg-teal text-off-black hover:bg-teal-dark focus:ring-2 focus:ring-teal/50 focus:outline-none rounded-md shadow-sm",
        glass:
          "bg-white/95 text-off-black border border-off-black/10 hover:bg-white hover:border-off-black/15 rounded-md shadow-sm",
        destructive:
          "bg-error-700 text-white hover:bg-error-500 active:bg-error-500 focus:ring-2 focus:ring-error-500/30 focus:outline-none rounded-md shadow-sm hover:shadow-md",
        outline:
          "border-2 border-off-black/15 bg-transparent text-off-black hover:bg-beige/30 hover:border-off-black/25 focus:ring-2 focus:ring-teal/50 focus:outline-none rounded-md",
        ghost:
          "text-off-black hover:bg-beige/30 focus:ring-2 focus:ring-teal/30 focus:outline-none rounded-md",
        link: "text-off-black underline-offset-4 hover:underline hover:text-orange-cta focus:outline-none",
      },
      size: {
        default: "h-10 px-6 py-2 rounded-md",
        sm: "h-8 px-4 text-xs rounded-s",
        lg: "h-12 px-8 text-sm rounded-lg",
        xl: "h-14 px-10 text-base rounded-lg",
        pill: "h-12 px-10 text-sm rounded-md",
        icon: "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size, className }))
    const shouldReduceMotion = prefersReducedMotion()

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as any, {
        className: cn((children as any).props.className, classes),
        "data-slot": "button",
        ...props,
      })
    }

    // framer-motion's motion.button overloads onDrag/onAnimation* with
    // gesture-specific signatures that conflict with React's native drag/
    // animation event types. Strip them from the spread; none of our callers
    // rely on native drag/animation events on Button.
    const {
      onDrag: _onDrag,
      onDragStart: _onDragStart,
      onDragEnd: _onDragEnd,
      onAnimationStart: _onAnimationStart,
      onAnimationEnd: _onAnimationEnd,
      onAnimationIteration: _onAnimationIteration,
      ...motionProps
    } = props
    void _onDrag; void _onDragStart; void _onDragEnd
    void _onAnimationStart; void _onAnimationEnd; void _onAnimationIteration

    return (
      <motion.button
        ref={ref}
        data-slot="button"
        className={cn(classes, loading && "cursor-not-allowed")}
        disabled={props.disabled || loading}
        whileHover={shouldReduceMotion ? {} : { scale: 1.005 }}
        whileTap={shouldReduceMotion ? {} : { scale: 0.995 }}
        transition={{ duration: 0.2 }}
        {...motionProps}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? <span className="ml-1">Loading...</span> : children}
      </motion.button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
